/** @module @liquidefy/code-editor/terminal/terminal @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  TerminalState,
  TerminalStatus,
  TerminalType,
  TerminalProfile,
  TerminalId,
  ShellType,
  EditorResult,
  EditorEvent,
  TerminalMetrics,
  TerminalSecurityContext,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asTerminalId,
  asTerminalSessionId,
  asTerminalBufferId,
  asPTYProcessId,
  now,
  emptyTerminalState,
  emptyTerminalSessionState,
  emptyTerminalBufferState,
  emptyPTYState,
  DEFAULT_TERMINAL_PROFILE,
  mergeTerminalProfile,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 TERMINAL STATUS TRANSITIONS — valid transitions for terminal status machine
// ═══════════════════════════════════════════════════════════════════════════════

/** Valid transitions for each TerminalStatus. */
export const VALID_TERMINAL_TRANSITIONS: Record<TerminalStatus, TerminalStatus[]> = {
  'initializing': ['connecting', 'active', 'error', 'closed'],
  'connecting':   ['active', 'disconnected', 'error', 'closed'],
  'active':       ['busy', 'disconnected', 'suspended', 'error', 'closed'],
  'busy':         ['active', 'disconnected', 'suspended', 'error', 'closed'],
  'disconnected': ['connecting', 'active', 'closed', 'error'],
  'suspended':    ['active', 'closed', 'error'],
  'error':        ['initializing', 'connecting', 'closed'],
  'closed':       ['initializing'],
};

/**
 * Perform a validated status transition on a terminal.
 * Returns err if the transition is not permitted by the state machine.
 */
function transitionStatus(
  state: TerminalState,
  next: TerminalStatus
): EditorResult<TerminalState> {
  const allowed = VALID_TERMINAL_TRANSITIONS[state.status];
  if (!allowed || !allowed.includes(next)) {
    return err(
      'TERMINAL_INVALID_TRANSITION',
      `Cannot transition terminal '${state.id}' from '${state.status}' to '${next}'`
    );
  }
  const events: EditorEvent[] = [{
    kind: next === 'closed' ? 'closed' : next === 'active' ? 'activated' : 'changed',
    domain: 'terminal',
    timestamp: now(),
    payload: { terminalId: state.id, status: next },
  }];
  return ok({ ...state, status: next }, 'TERMINAL_STATUS_CHANGED', `Terminal transitioned to '${next}'`, events);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 TERMINAL LIFECYCLE — create, close, focus, status updates
// ═══════════════════════════════════════════════════════════════════════════════

/** Configuration passed to createTerminal. */
export interface CreateTerminalConfig {
  readonly name?: string;
  readonly type?: TerminalType;
  readonly shell?: ShellType;
  readonly cwd?: string;
  readonly env?: Record<string, string>;
  readonly profile?: Partial<TerminalProfile>;
  readonly userId?: string;
}

/**
 * Create a new terminal state with the given config.
 * Merges the supplied profile fragment onto the default profile.
 */
export function createTerminal(
  config: CreateTerminalConfig
): EditorResult<TerminalState> {
  const id = asTerminalId(generateId('term'));
  const profile = config.profile
    ? mergeTerminalProfile(DEFAULT_TERMINAL_PROFILE, config.profile as TerminalProfile)
    : DEFAULT_TERMINAL_PROFILE;

  const shell = config.shell ?? profile.shell;
  const sessionId = asTerminalSessionId(generateId('ts'));
  const bufferId = asTerminalBufferId(generateId('tb'));
  const ptyId = asPTYProcessId(generateId('pty'));

  const session = {
    ...emptyTerminalSessionState(sessionId),
    shell,
    cwd: config.cwd ?? profile.cwd ?? '',
    env: { ...profile.env, ...(config.env ?? {}) },
  };

  const pty = {
    ...emptyPTYState(ptyId),
    shell,
    cwd: config.cwd ?? profile.cwd ?? '',
  };

  const securityContext: TerminalSecurityContext = {
    userId: config.userId ?? '',
    permissions: [],
    commandRiskLevel: 'safe',
  };

  const state: TerminalState = {
    ...emptyTerminalState(id),
    name: config.name ?? `${shell} terminal`,
    type: config.type ?? 'local',
    status: 'initializing',
    session,
    buffer: emptyTerminalBufferState(bufferId),
    pty,
    profile,
    securityContext,
    metrics: { cpuUsage: 0, memoryUsage: 0, bytesRead: 0, bytesWritten: 0, throughput: 0 },
  };

  const events: EditorEvent[] = [{
    kind: 'created',
    domain: 'terminal',
    timestamp: now(),
    payload: { terminalId: id, type: state.type, shell },
  }];

  return ok(state, 'TERMINAL_CREATED', `Terminal '${state.name}' created`, events);
}

/**
 * Look up a terminal by ID from a collection.
 * Returns err if the terminal is not found.
 */
export function getTerminalById(
  terminals: TerminalState[],
  id: TerminalId
): EditorResult<TerminalState> {
  const found = terminals.find(t => t.id === id);
  if (!found) return err('TERMINAL_NOT_FOUND', `Terminal '${id}' not found`);
  return ok(found, 'TERMINAL_FOUND', `Terminal '${id}' retrieved`);
}

/**
 * Update the status of a terminal in a collection.
 * Returns updated collection on success, err on invalid transition.
 */
export function updateTerminalStatus(
  terminals: TerminalState[],
  id: TerminalId,
  status: TerminalStatus
): EditorResult<TerminalState[]> {
  const idx = terminals.findIndex(t => t.id === id);
  if (idx === -1) return err('TERMINAL_NOT_FOUND', `Terminal '${id}' not found`);
  const terminal = terminals[idx]!;
  const result = transitionStatus(terminal, status);
  if (!result.success) return result as unknown as EditorResult<TerminalState[]>;
  const updated = [...terminals];
  updated[idx] = result.value;
  return ok(updated, 'TERMINAL_STATUS_UPDATED', `Terminal '${id}' → '${status}'`, result.events);
}

/**
 * Close a terminal — transitions to 'closed' and returns the updated collection.
 * The caller is responsible for releasing PTY and process resources via IO.
 */
export function closeTerminal(
  terminals: TerminalState[],
  id: TerminalId
): EditorResult<TerminalState[]> {
  return updateTerminalStatus(terminals, id, 'closed');
}

/**
 * Mark a terminal as the focused/active one.
 * Stores the focused state via the status ('active') but does not affect the
 * data model directly — focus is tracked externally. This sets the target
 * terminal to 'active' if it is currently in a compatible state.
 */
export function focusTerminal(
  terminals: TerminalState[],
  id: TerminalId
): EditorResult<TerminalState[]> {
  const idx = terminals.findIndex(t => t.id === id);
  if (idx === -1) return err('TERMINAL_NOT_FOUND', `Terminal '${id}' not found`);
  const terminal = terminals[idx]!;

  // Only transition non-closed, non-error terminals
  if (terminal.status === 'closed' || terminal.status === 'error') {
    return err('TERMINAL_NOT_FOCUSABLE', `Cannot focus terminal in status '${terminal.status}'`);
  }
  if (terminal.status === 'active' || terminal.status === 'busy') {
    // Already active — emit event without state change
    const events: EditorEvent[] = [{
      kind: 'activated',
      domain: 'terminal',
      timestamp: now(),
      payload: { terminalId: id },
    }];
    return ok(terminals, 'TERMINAL_FOCUSED', `Terminal '${id}' already active`, events);
  }

  return updateTerminalStatus(terminals, id, 'active');
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 TERMINAL QUERIES — active, list, stats
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Return the first terminal that is 'active' or 'busy'.
 * Returns err if no active terminal exists.
 */
export function getActiveTerminal(
  terminals: TerminalState[]
): EditorResult<TerminalState> {
  const active = terminals.find(t => t.status === 'active' || t.status === 'busy');
  if (!active) return err('TERMINAL_NONE_ACTIVE', 'No active terminal found');
  return ok(active, 'TERMINAL_ACTIVE_FOUND', `Active terminal: '${active.id}'`);
}

/**
 * List all terminals, optionally filtered by status.
 */
export function listTerminals(
  terminals: TerminalState[],
  filter?: { status?: TerminalStatus; type?: TerminalType }
): TerminalState[] {
  let result = terminals;
  if (filter?.status !== undefined) {
    result = result.filter(t => t.status === filter.status);
  }
  if (filter?.type !== undefined) {
    result = result.filter(t => t.type === filter.type);
  }
  return result;
}

/**
 * Compute stats across a terminal collection: counts by type and status.
 */
export function getTerminalStats(
  terminals: TerminalState[]
): {
  total: number;
  byStatus: Partial<Record<TerminalStatus, number>>;
  byType: Partial<Record<TerminalType, number>>;
  active: number;
  closed: number;
} {
  const byStatus: Partial<Record<TerminalStatus, number>> = {};
  const byType: Partial<Record<TerminalType, number>> = {};

  for (const t of terminals) {
    byStatus[t.status] = (byStatus[t.status] ?? 0) + 1;
    byType[t.type] = (byType[t.type] ?? 0) + 1;
  }

  return {
    total: terminals.length,
    byStatus,
    byType,
    active: terminals.filter(t => t.status === 'active' || t.status === 'busy').length,
    closed: terminals.filter(t => t.status === 'closed').length,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 TERMINAL PROFILE — factory and application
// ═══════════════════════════════════════════════════════════════════════════════

/** Partial config accepted by createTerminalProfile. */
export interface TerminalProfileConfig {
  readonly name: string;
  readonly shell: ShellType;
  readonly args?: string[];
  readonly env?: Record<string, string>;
  readonly cwd?: string;
  readonly overrides?: Partial<TerminalProfile>;
}

/**
 * Build a TerminalProfile from a config fragment.
 * Merges provided values onto the default profile.
 */
export function createTerminalProfile(config: TerminalProfileConfig): TerminalProfile {
  const base: TerminalProfile = {
    ...DEFAULT_TERMINAL_PROFILE,
    name: config.name,
    shell: config.shell,
    args: config.args ?? DEFAULT_TERMINAL_PROFILE.args,
    env: { ...DEFAULT_TERMINAL_PROFILE.env, ...(config.env ?? {}) },
    cwd: config.cwd ?? DEFAULT_TERMINAL_PROFILE.cwd,
  };
  if (config.overrides) {
    return mergeTerminalProfile(base, config.overrides as TerminalProfile);
  }
  return base;
}

/**
 * Apply a profile to a specific terminal, updating its session shell, cwd,
 * env, and rendering/behavior config. Returns the updated terminal.
 */
export function applyProfile(
  terminals: TerminalState[],
  id: TerminalId,
  profile: TerminalProfile
): EditorResult<TerminalState[]> {
  const idx = terminals.findIndex(t => t.id === id);
  if (idx === -1) return err('TERMINAL_NOT_FOUND', `Terminal '${id}' not found`);

  const terminal = terminals[idx]!;
  const updatedSession = {
    ...terminal.session,
    shell: profile.shell,
    cwd: profile.cwd || terminal.session.cwd,
    env: { ...terminal.session.env, ...profile.env },
  };

  const updated: TerminalState = {
    ...terminal,
    profile,
    session: updatedSession,
    name: terminal.name !== 'Terminal' ? terminal.name : profile.name,
  };

  const result = [...terminals];
  result[idx] = updated;

  const events: EditorEvent[] = [{
    kind: 'changed',
    domain: 'terminal',
    timestamp: now(),
    payload: { terminalId: id, profileName: profile.name },
  }];

  return ok(result, 'TERMINAL_PROFILE_APPLIED', `Profile '${profile.name}' applied to terminal '${id}'`, events);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 METRICS — update and query resource metrics
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Update the resource metrics snapshot for a terminal.
 */
export function updateTerminalMetrics(
  terminals: TerminalState[],
  id: TerminalId,
  metrics: Partial<TerminalMetrics>
): EditorResult<TerminalState[]> {
  const idx = terminals.findIndex(t => t.id === id);
  if (idx === -1) return err('TERMINAL_NOT_FOUND', `Terminal '${id}' not found`);
  const terminal = terminals[idx]!;
  const updated = [...terminals];
  updated[idx] = { ...terminal, metrics: { ...terminal.metrics, ...metrics } };
  return ok(updated, 'TERMINAL_METRICS_UPDATED', `Metrics updated for terminal '${id}'`);
}

/**
 * Return aggregate metrics across all terminals (totals for throughput and I/O).
 */
export function aggregateTerminalMetrics(
  terminals: TerminalState[]
): TerminalMetrics {
  return terminals.reduce<TerminalMetrics>(
    (acc, t) => ({
      cpuUsage: acc.cpuUsage + t.metrics.cpuUsage,
      memoryUsage: acc.memoryUsage + t.metrics.memoryUsage,
      bytesRead: acc.bytesRead + t.metrics.bytesRead,
      bytesWritten: acc.bytesWritten + t.metrics.bytesWritten,
      throughput: acc.throughput + t.metrics.throughput,
    }),
    { cpuUsage: 0, memoryUsage: 0, bytesRead: 0, bytesWritten: 0, throughput: 0 }
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 SUMMARY — human-readable snapshot of terminal state
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Produce a concise summary of the terminal collection for diagnostics and logging.
 */
export function summarizeTerminals(
  terminals: TerminalState[]
): {
  totalTerminals: number;
  activeTerminals: number;
  closedTerminals: number;
  errorTerminals: number;
  byType: Partial<Record<TerminalType, number>>;
  aggregateMetrics: TerminalMetrics;
} {
  const stats = getTerminalStats(terminals);
  return {
    totalTerminals: stats.total,
    activeTerminals: stats.active,
    closedTerminals: stats.closed,
    errorTerminals: stats.byStatus['error'] ?? 0,
    byType: stats.byType,
    aggregateMetrics: aggregateTerminalMetrics(terminals),
  };
}
